// =====================================================================
// Was there ever a two-sided conversation — and if not, which side was missing?
//
// The cockpit already receives a live volume stream from the Twilio SDK
// (`call.on('volume', (input, output))`). Until now it fed only the silent-voicemail
// guard and a UI label, and was discarded at teardown. This module keeps the running
// facts so every call can report them.
//
// THE LOAD-BEARING FACT: our blip mute silences PLAYBACK only (`el.muted = true`,
// `gain.gain.value = 0`). The SDK derives `output` from the remote MediaStream via its
// own AnalyserNode (voice-sdk `peerconnection.js`, `_updateOutputStreamSource(this._remoteStream)`),
// so the level keeps reporting the lead's real speech while the SDR hears nothing.
// `remoteWhileMutedMs` is therefore DIRECT evidence of "the lead was talking into a
// closed speaker", not an inference.
//
// Pure and timer-free: the caller owns `performance.now()` and the audibility
// thresholds (cockpit/voicemail.ts), so the rules test without a Twilio Call.
// =====================================================================

export interface AudioLiveness {
  // When the call was accepted, in the caller's clock. The origin for
  // `firstRemoteAudibleMs`, which is the only measure of when the SDR could ACTUALLY
  // hear the lead — as opposed to when we stopped preventing it.
  startedAt: number | null;
  // Ms from accept to the first far-side audio, MUTED OR NOT. A call that accepts
  // promptly, un-mutes promptly, and still carries no media for seconds (WebRTC setup
  // on a poor link) looks healthy on every other field here and is silence to the SDR.
  firstRemoteAudibleMs: number | null;
  // When the speaker was closed, in the caller's clock. Null while open.
  mutedSince: number | null;
  // Ms from the speaker closing to the FIRST moment the lead was audible through it.
  // Null when that never happened. This is "how long they talked before we could hear".
  firstRemoteWhileMutedMs: number | null;
  // Accumulated ms during which the lead was audible AND the speaker was shut.
  remoteWhileMutedMs: number;
  // How many samples landed in that state — a crude confidence measure, since the
  // accumulation is only as fine-grained as the SDK's emit cadence.
  remoteWhileMutedSamples: number;
  // Ever audible at all, regardless of mute. The "was the lead there" half.
  remoteEverAudible: boolean;
  // Ever audible from our mic. The "did the SDR speak" half.
  localEverAudible: boolean;
  // Internal: the previous sample's timestamp, to accumulate an interval rather than
  // count samples. Null until the first sample.
  lastSampleAt: number | null;
  // Internal: was the PREVIOUS sample audible-while-muted? An interval is only credited
  // when both ends of it were, so the silence before they started talking is not counted
  // as time they spent unheard — `firstRemoteWhileMutedMs` is what measures that.
  prevAudibleWhileMuted: boolean;
}

// `startedAt` is the accept moment. Passed in rather than read here so the module stays
// clock-free and testable; omit it only where there is genuinely no call yet.
export function newAudioLiveness(startedAt: number | null = null): AudioLiveness {
  return {
    startedAt,
    firstRemoteAudibleMs: null,
    mutedSince: null,
    firstRemoteWhileMutedMs: null,
    remoteWhileMutedMs: 0,
    remoteWhileMutedSamples: 0,
    remoteEverAudible: false,
    localEverAudible: false,
    lastSampleAt: null,
    prevAudibleWhileMuted: false,
  };
}

// The speaker just closed. Idempotent: re-asserting the mute (the `audio` handler does
// this when the playback element arrives after accept) must not restart the clock, or
// `firstRemoteWhileMutedMs` would measure from the wrong origin.
export function markMuted(state: AudioLiveness, now: number): void {
  if (state.mutedSince == null) state.mutedSince = now;
}

// The speaker reopened. Leaves every accumulated fact intact — they are the report.
export function markUnmuted(state: AudioLiveness): void {
  state.mutedSince = null;
}

export interface LivenessSample {
  // Far-side level cleared the remote threshold (caller applies voicemail.ts's rule).
  remoteAudible: boolean;
  // Our mic cleared the local threshold, already accounting for an SDR-side mute.
  localAudible: boolean;
  now: number;
}

// Fold one volume sample in.
//
// The muted-while-audible interval is accumulated from the GAP BETWEEN SAMPLES rather
// than by counting them: the SDK's emit cadence is not contractual, so a sample count
// would silently change meaning if it moved. Only a gap whose BOTH ends were
// audible-while-muted is credited — crediting a gap that merely ended in audio would
// bill the silence before they spoke as time they spent unheard, which is what
// `firstRemoteWhileMutedMs` measures separately.
export function observe(state: AudioLiveness, sample: LivenessSample): void {
  const { remoteAudible, localAudible, now } = sample;
  if (remoteAudible) {
    state.remoteEverAudible = true;
    if (state.firstRemoteAudibleMs == null && state.startedAt != null) {
      state.firstRemoteAudibleMs = Math.max(0, Math.round(now - state.startedAt));
    }
  }
  if (localAudible) state.localEverAudible = true;

  const audibleWhileMuted = remoteAudible && state.mutedSince != null;
  if (audibleWhileMuted) {
    state.remoteWhileMutedSamples += 1;
    if (state.firstRemoteWhileMutedMs == null) {
      state.firstRemoteWhileMutedMs = Math.max(0, Math.round(now - (state.mutedSince as number)));
    }
    if (state.prevAudibleWhileMuted && state.lastSampleAt != null) {
      state.remoteWhileMutedMs += Math.max(0, Math.round(now - state.lastSampleAt));
    }
  }
  state.prevAudibleWhileMuted = audibleWhileMuted;
  state.lastSampleAt = now;
}

// How this call's audio actually went. `oneWay` is the headline: the lead was audible
// while the SDR's speaker was shut, which is the reported symptom stated as a fact.
export interface LivenessReport {
  one_way: boolean;
  // Accept → the SDR could actually hear the lead. Covers BOTH ways that fails: a mute
  // held too long, and media that never arrived. Null when the lead was never audible.
  first_remote_audible_ms: number | null;
  first_remote_while_muted_ms: number | null;
  remote_while_muted_ms: number;
  remote_while_muted_samples: number;
  remote_ever_audible: boolean;
  local_ever_audible: boolean;
  // Neither side ever made a sound — dead air in both directions, a different fault
  // from one-way audio and worth separating in the data.
  silent_both_ways: boolean;
}

export function report(state: AudioLiveness): LivenessReport {
  return {
    one_way: state.remoteWhileMutedSamples > 0,
    first_remote_audible_ms: state.firstRemoteAudibleMs,
    first_remote_while_muted_ms: state.firstRemoteWhileMutedMs,
    remote_while_muted_ms: state.remoteWhileMutedMs,
    remote_while_muted_samples: state.remoteWhileMutedSamples,
    remote_ever_audible: state.remoteEverAudible,
    local_ever_audible: state.localEverAudible,
    silent_both_ways: !state.remoteEverAudible && !state.localEverAudible,
  };
}
